/**
 * The playground's address, kept in the URL's hash.
 *
 *   #/connect                  key capsule, personas, sign-up hand-off, verify socket
 *   #/stage                    the constellation (the first run while there are no Worlds)
 *   #/w/{w}[?date=&hour=]      one World up close
 *   #/w/{w}/relays/{r}         the Loom, on the planner
 *   #/w/{w}/inbox              a hand's inbox
 *   #/gallery   #/account
 *   #/verify?token=sbv_…       the mailed link
 *   #/try[?token=…]            try it with no key: a sandbox (the token is the human check's)
 *
 * A hash needs no server rewrites, so the built files serve from any static
 * host, and a verify token in it never reaches a server or a Referer.
 */

import type { CreateRouter, Route, Router } from '../contracts';

const DATE = /^\d{4}-\d{2}-\d{2}$/;

const decode = (part: string): string => {
  try {
    return decodeURIComponent(part);
  } catch {
    return part;
  }
};

const enc = encodeURIComponent;

/** The route a hash names. Anything unknown is the stage, which asks for a key when there is none. */
export const parseRoute = (hash: string): Route => {
  const bare = hash.replace(/^#\/?/, '');
  const cut = bare.indexOf('?');
  const path = cut < 0 ? bare : bare.slice(0, cut);
  const params = new URLSearchParams(cut < 0 ? '' : bare.slice(cut + 1));
  const parts = path.split('/').filter(Boolean).map(decode);
  switch (parts[0]) {
    case 'connect': return { name: 'connect' };
    case 'gallery': return { name: 'gallery' };
    case 'account': return { name: 'account' };
    case 'verify': return { name: 'verify', token: params.get('token') ?? '' };
    case 'try': return { name: 'try', token: params.get('token') ?? '' };
    case 'w': {
      const world = parts[1];
      if (!world) return { name: 'stage' };
      if (parts[2] === 'relays' && parts[3]) return { name: 'relay', world, relay: parts[3] };
      if (parts[2] === 'inbox') return { name: 'inbox', world };
      const date = params.get('date');
      const hourText = params.get('hour');
      const hour = hourText !== null && /^\d{1,2}$/.test(hourText) ? Number(hourText) : undefined;
      return {
        name: 'world', world,
        ...(date && DATE.test(date) ? { date } : {}),
        ...(hour !== undefined && hour <= 23 ? { hour } : {}),
      };
    }
    default: return { name: 'stage' };
  }
};

/** The hash for a route; `parseRoute(routeHref(r))` gives `r` back. */
export const routeHref = (route: Route): string => {
  switch (route.name) {
    case 'connect': return '#/connect';
    case 'stage': return '#/stage';
    case 'gallery': return '#/gallery';
    case 'account': return '#/account';
    case 'verify': return route.token ? `#/verify?token=${enc(route.token)}` : '#/verify';
    case 'try': return route.token ? `#/try?token=${enc(route.token)}` : '#/try';
    case 'relay': return `#/w/${enc(route.world)}/relays/${enc(route.relay)}`;
    case 'inbox': return `#/w/${enc(route.world)}/inbox`;
    case 'world': {
      const q = new URLSearchParams();
      if (route.date) q.set('date', route.date);
      if (route.hour !== undefined) q.set('hour', String(route.hour));
      const s = q.toString();
      return `#/w/${enc(route.world)}${s ? `?${s}` : ''}`;
    }
  }
};

/** Routes a visitor without a key may open. */
export const OPEN_ROUTES: readonly Route['name'][] = ['connect', 'verify', 'try'];
export const needsKey = (route: Route): boolean => !OPEN_ROUTES.includes(route.name);

/** The World a route is about, if any: what focus, links and presence follow. */
export const worldOfRoute = (route: Route): string | undefined =>
  route.name === 'world' || route.name === 'relay' || route.name === 'inbox' ? route.world : undefined;


/** What the router needs of a browser window; tests hand in a fake. */
export type RouterWindow = Pick<globalThis.Window, 'location' | 'history' | 'addEventListener'>;

/**
 * The router over a window. `go` to the hash already shown still tells every
 * listener, so a screen can be re-entered; `replace` keeps the step out of
 * history (a spent verify token, a redirect to Connect).
 */
export const createRouterOn = (w: RouterWindow): Router => {
  const listeners = new Set<(r: Route) => void>();
  let current = parseRoute(w.location.hash);
  const emit = (): void => {
    current = parseRoute(w.location.hash);
    for (const fn of [...listeners]) fn(current);
  };
  w.addEventListener('hashchange', emit);
  const router: Router = {
    current: () => current,
    go(route, replace = false) {
      const href = routeHref(route);
      if (replace || w.location.hash === href) {
        w.history.replaceState(w.history.state, '', href);
        emit();
      } else {
        w.location.hash = href;
      }
    },
    on(fn) {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
    href: routeHref,
  };
  return router;
};

/** The contract's factory: the page's window unless one is handed in. */
export const createRouter: CreateRouter = (win) => createRouterOn(win ?? window);

/** A name for a relay not laid yet: the Loom's builder lets it be renamed before LAY. */
export const freshRelayName = (now: number = Date.now()): string => `relay-${now.toString(36).slice(-5)}`;
